import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Modal,
  Backdrop,
  Fade,
  Typography,
  Button,
  TextField,
  Box,
  makeStyles,
  Snackbar
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { forgotPasswordSendOtp, forgotPasswordValidateOtp, forgotPassword } from "../../action/password";
import { validatePassword } from "../../utils/general";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const useStyles = makeStyles((theme) => ({
  modalRoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: "#fff",
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    minWidth: 360,
    outline: "none",
  },
  field: {
    marginTop: theme.spacing(2),
  },
  actions: {
    marginTop: theme.spacing(3),
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
  },
}));

const ForgotPassword = ({
  open,
  onClose,
  forgotPasswordSendOtp,
  forgotPasswordValidateOtp,
  forgotPassword,
}) => {
  const classes = useStyles();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");
  const [counter, setCounter] = useState(30);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const showToast = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (step === 2) setCounter(30);
  }, [step]);

  useEffect(() => {
    if (step !== 2 || counter <= 0) return;

    const timerId = setInterval(() => {
      setCounter((c) => c - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [step, counter]);

  const handleReset = () => {
    setStep(1);
    setEmail("");
    setOtp("");
    setError("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const sendOtp = () => {
    setError("");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    if(!emailRegex.test(email)){
      setError("Please enter a valid email address.")
      return;
    }

    forgotPasswordSendOtp(email)
      .then((res) => {
        if (res?.status === 200) {
          setError("");
          setStep(2);
        } else {
          showToast("Unable to send OTP. Please try again.");
        }
      })
      .catch((err) => {
        if (err.response.status === 500 || err.response.status === 429) {
          showToast(err.response.data.message, "danger");
        } else {
          showToast("Failed to send OTP.");
        }
      });
  };

  const verifyOtp = () => {
    setError("");
    if (!otp) {
      setError("Please enter OTP.");
      return;
    }
    if(otp.length!==6){
      setError("Please enter valid otp.")
      return;
    }

    forgotPasswordValidateOtp(email, otp)
      .then((res) => {
        if (res?.status === 200) {
          setToken(res?.data?.token);
          setError("");
          setStep(3);
        } else {
          showToast("Unable to validate OTP. Please try again.");
        }
      })
      .catch((err) => {
        if (err.response.status === 500) {
          showToast(err.response.data.message, "danger");
        } else {
          showToast("OTP verification failed.");
        }
      });
  };

  const resetPassword = () => {
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("Please fill both fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const { valid, error: validationError } = validatePassword(newPassword);
    if (!valid) {
      setError(validationError);
      return;
    }

    forgotPassword(email, newPassword, token)
      .then((res) => {
        if (res?.status === 200) {
          setError("");
          setSuccess("Your password has been reset successfully. Please sign in again.");
          showToast("Password reset successful!", "success");
          setNewPassword("");
          setConfirmPassword("");
          onClose();
        } else {
          showToast("Unable to reset password. Please try again.");
        }
      })
      .catch((err) => {
        if (err.response.status === 500) {
          showToast(err.response.data.message, "danger");
        } else {
          showToast("Password reset failed.");
        }
      });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={classes.modalRoot}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={open}>
        <Box className={classes.paper}>
          {step === 1 && (
            <>
              <Typography variant="h6" gutterBottom>
                Reset Password
              </Typography>
              <Typography variant="body2">
                Enter your registered email address to receive an OTP.
              </Typography>
              <TextField
                className={classes.field}
                label="Email Address"
                type="email"
                fullWidth
                InputProps={{ disableUnderline: true }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
              {error && (
                <Typography color="error" variant="body2" className={classes.field}>
                  {error}
                </Typography>
              )}
              <Box className={classes.actions}>
                <Button onClick={handleClose}>Cancel</Button>
                <Button variant="contained" color="primary" onClick={sendOtp}>
                  Continue
                </Button>
              </Box>
            </>
          )}

          {step === 2 && (
            <>
              <Typography variant="h6" gutterBottom>
                Enter OTP
              </Typography>
              <Typography variant="body2">
                We sent an OTP to <span style = {{fontWeight: 'bold'}}>{email}</span>
              </Typography>
              <TextField
                className={classes.field}
                label="OTP"
                type="text"
                fullWidth
                InputProps={{ disableUnderline: true }}
                value={otp}
                onChange={(e) => {
                  if (/^[a-zA-Z0-9]{0,6}$/.test(e.target.value)) {
                    setOtp(e.target.value);
                  }
              
                }}
                autoFocus
              />
              <Box mt={1}>
                {counter > 0 ? (
                  <Typography variant="caption" color="textSecondary">
                    Resend OTP in {counter}s
                  </Typography>
                ) : (
                  <Typography variant="caption">
                    Didn't receive it?{" "}
                    <a
                      href="#"
                      onClick={() => {
                        sendOtp();
                        setOtp("");
                        setError("");
                        setCounter(30);
                      }}
                    >
                      Resend OTP
                    </a>
                  </Typography>
                )}
              </Box>
              {error && (
                <Typography color="error" variant="body2" className={classes.field}>
                  {error}
                </Typography>
              )}
              <Box className={classes.actions}>
                <Button
                  onClick={() => {
                    setStep(1);
                    setOtp("");
                    setError("");
                  }}
                >
                  Back
                </Button>
                <Button variant="contained" color="primary" onClick={verifyOtp}>
                  Continue
                </Button>
              </Box>
            </>
          )}

          {step === 3 && (
            <>
              <Typography variant="h6" gutterBottom>
                Set New Password
              </Typography>
              <TextField
                className={classes.field}
                label="New Password"
                type="password"
                fullWidth
                InputProps={{ disableUnderline: true }}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
              />
              <TextField
                className={classes.field}
                label="Confirm Password"
                type="password"
                fullWidth
                InputProps={{ disableUnderline: true }}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {error && (
                <Typography color="error" variant="body2" className={classes.field}>
                  {error}
                </Typography>
              )}
              {success && (
                <Typography color="primary" variant="body2" className={classes.field}>
                  {success}
                </Typography>
              )}
              <Box className={classes.actions}>
                <Button
                  onClick={() => {
                    setStep(1);
                    setNewPassword("");
                    setConfirmPassword("");
                    setError("");
                  }}
                >
                  Back
                </Button>
                <Button variant="contained" color="primary" onClick={resetPassword}>
                  Submit
                </Button>
              </Box>
            </>
          )}

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Fade>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch) => ({
  forgotPasswordSendOtp: (email) => dispatch(forgotPasswordSendOtp(email)),
  forgotPasswordValidateOtp: (email, otp) => dispatch(forgotPasswordValidateOtp(email, otp)),
  forgotPassword: (email, newPassword, token) =>
    dispatch(forgotPassword(email, newPassword, token)),
});

export default connect(null, mapDispatchToProps)(ForgotPassword);
